/**
 * MeInventory — Phase 4 home inventory
 *   /me/health/inventory
 *   Predictive "N days of X left" alerts. Ties to Rescigo pickup.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, Package, Warning, Plus, Trash, ShoppingBag,
  CheckCircle, ArrowRight,
} from "@phosphor-icons/react";

const CATEGORIES = [
  { v: "diapers_baby",   l: "Baby diapers",   e: "🍼", color: "#2563EB" },
  { v: "diapers_adult",  l: "Adult diapers",  e: "👴", color: "#7C3AED" },
  { v: "sanitary_pads",  l: "Sanitary pads",  e: "🌸", color: "#EC4899" },
  { v: "gloves",         l: "Gloves",         e: "🧤", color: "#0F766E" },
  { v: "masks",          l: "Masks",          e: "😷", color: "#0891B2" },
  { v: "medicine",       l: "Medicine",       e: "💊", color: "#DC2626" },
  { v: "pet_food",       l: "Pet food",       e: "🐾", color: "#B45309" },
  { v: "milk",           l: "Milk / formula", e: "🥛", color: "#EA580C" },
  { v: "wipes",          l: "Wipes",          e: "🧻", color: "#15803D" },
  { v: "other",          l: "Other",          e: "📦", color: "#5C5A55" },
];

function BackHeader({ to, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <Link to={to} className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-50">
        <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
      </Link>
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-black text-[22px] leading-tight tracking-tight text-[#111827]">
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

export function HomeInventory() {
  const [data, setData] = useState({ items: [], low_items: [], low_count: 0 });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank());

  function blank() {
    return { item_key: "", name: "", category: "diapers_baby", stock_count: "", unit: "pcs", daily_use: "", low_threshold_days: 3 };
  }

  const load = async () => {
    try {
      const r = await custApi.get("/me/inventory/summary");
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.stock_count || !form.daily_use) {
      return toast.error("Name, stock and daily use required");
    }
    try {
      await custApi.post("/me/inventory", {
        ...form,
        item_key: (form.item_key || form.name).toLowerCase().replace(/\s+/g, "_"),
        stock_count: Number(form.stock_count),
        daily_use: Number(form.daily_use),
        low_threshold_days: Number(form.low_threshold_days),
      });
      toast.success("Saved");
      setForm(blank());
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await custApi.delete(`/me/inventory/${id}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-inventory">
      <BackHeader to="/me/health" title="Home inventory"
                    subtitle="Predictive stock alerts"/>

      {/* Low-stock hero */}
      {data.low_count > 0 ? (
        <div className="rounded-3xl p-4 bg-gradient-to-br from-[#DC2626] via-[#B91C1C] to-[#7F1D1D] text-white shadow-[0_20px_50px_rgba(220,38,38,0.30)]"
             data-testid="inv-low-hero">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 inline-flex items-center justify-center">
              <Warning size={22} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Restock alert</div>
              <div className="font-display font-black text-lg leading-tight">
                {data.low_count} item{data.low_count > 1 ? "s" : ""} running low
              </div>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {data.low_items.slice(0, 3).map(it => (
              <div key={it.id} className="flex items-center gap-2 bg-white/15 backdrop-blur rounded-lg px-3 py-1.5">
                <span className="text-lg">{CATEGORIES.find(c => c.v === it.category)?.e || "📦"}</span>
                <div className="flex-1 text-[12px] font-semibold truncate">{it.name}</div>
                <div className="text-[11px] font-bold">{it.days_left != null ? `${it.days_left}d left` : "?"}</div>
              </div>
            ))}
          </div>
          <Link to="/me/services" data-testid="inv-shop"
                className="mt-3 h-10 w-full inline-flex items-center justify-center bg-white text-[#DC2626] rounded-xl text-sm font-bold gap-1.5">
            <ShoppingBag size={14} weight="fill"/> Buy more · Rescigo shop
          </Link>
        </div>
      ) : data.items.length > 0 ? (
        <div className="rounded-2xl p-3 bg-[#DCFCE7] border border-green-200 flex items-center gap-3">
          <CheckCircle size={18} weight="fill" className="text-[#15803D] shrink-0"/>
          <div className="text-[12px] font-medium text-[#15803D]">
            All items stocked. Nice work planning ahead!
          </div>
        </div>
      ) : null}

      {/* Add button */}
      <button onClick={() => setShowForm(true)}
              data-testid="inv-add"
              className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-xl text-sm font-medium inline-flex items-center justify-center gap-1.5">
        <Plus size={13} weight="bold"/> Track an item
      </button>

      {showForm && (
        <div className="border-2 border-dashed border-[#DC2626] rounded-md p-3 bg-[#FEF7F6] space-y-2" data-testid="inv-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Category</label>
            <select value={form.category}
                    onChange={e => setForm(s => ({ ...s, category: e.target.value }))}
                    data-testid="inv-category"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
              {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.e} {c.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Product name *</label>
            <input value={form.name}
                   onChange={e => setForm(s => ({ ...s, name: e.target.value }))}
                   data-testid="inv-name"
                   placeholder="e.g. Pampers Medium"
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Stock *</label>
              <input type="number" value={form.stock_count}
                     onChange={e => setForm(s => ({ ...s, stock_count: e.target.value }))}
                     data-testid="inv-stock"
                     placeholder="12"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Per day *</label>
              <input type="number" step="0.1" value={form.daily_use}
                     onChange={e => setForm(s => ({ ...s, daily_use: e.target.value }))}
                     data-testid="inv-daily"
                     placeholder="4"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Alert (d)</label>
              <input type="number" value={form.low_threshold_days}
                     onChange={e => setForm(s => ({ ...s, low_threshold_days: e.target.value }))}
                     data-testid="inv-threshold"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={save} data-testid="inv-save"
                    className="h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">Save</button>
            <button onClick={() => setShowForm(false)}
                    className="h-10 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Items list */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold mb-2">
          Tracked items ({data.items.length})
        </div>
        {data.items.length === 0 ? (
          <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
            <Package size={22} color="#8A8780" className="mx-auto mb-1"/>
            <div className="text-xs text-[#5C5A55]">
              Track diapers, medicines &amp; more. We&apos;ll warn you before you run out.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="inv-list">
            {data.items.map(it => {
              const cat = CATEGORIES.find(c => c.v === it.category) || CATEGORIES[9];
              return (
                <div key={it.id} data-testid={`inv-row-${it.id}`}
                     className={`border rounded-md p-3 flex items-center gap-3 ${
                       it.is_low ? "border-[#DC2626] bg-[#FEF7F6]" : "border-[#E5E2D9] bg-white"
                     }`}>
                  <div className="w-10 h-10 rounded-full text-2xl inline-flex items-center justify-center shrink-0"
                       style={{ backgroundColor: cat.color + "22" }}>{cat.e}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1A1A1A] truncate">{it.name}</div>
                    <div className="text-[10px] text-gray-500">
                      {it.stock_count} {it.unit} · uses {it.daily_use}/day
                    </div>
                  </div>
                  <div className={`text-right ${it.is_low ? "text-[#DC2626]" : "text-[#15803D]"}`}>
                    <div className="text-[10px] uppercase tracking-widest font-bold">
                      {it.days_left != null ? `${it.days_left}d` : "?"}
                    </div>
                    <div className="text-[9px] font-semibold uppercase tracking-widest">
                      {it.is_low ? "LOW" : "OK"}
                    </div>
                  </div>
                  <button onClick={() => remove(it.id)} className="p-1.5 rounded hover:bg-[#FEE2E2]">
                    <Trash size={12} color="#DC2626"/>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Care Circle — lightweight curated tips
// ══════════════════════════════════════════════════════════════════
export function CareCircle() {
  const [tips, setTips] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    custApi.get("/me/care-circle/tips").then(r => {
      setTips(r.data.tips || []);
      setCategories(r.data.categories || []);
    }).catch(() => {});
  }, []);

  const filtered = cat === "all" ? tips : tips.filter(t => t.category === cat);

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-care-circle">
      <BackHeader to="/me/health" title="Care Circle"
                    subtitle="Tips from trusted sources"/>
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4">
        {["all", ...categories].map(c => {
          const on = c === cat;
          return (
            <button key={c} onClick={() => setCat(c)}
                    data-testid={`cc-tab-${c}`}
                    className={`h-9 px-3 shrink-0 rounded-full text-xs font-semibold border ${
                      on ? "bg-[#DC2626] text-white border-[#DC2626]"
                         : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>
              {c === "all" ? "All" : c}
            </button>
          );
        })}
      </div>

      <div className="space-y-2" data-testid="cc-list">
        {filtered.map(t => (
          <div key={t.id} data-testid={`cc-tip-${t.id}`}
               className="bg-white rounded-2xl p-4 border border-gray-100 shadow-[0_4px_12px_rgba(0,0,0,0.03)]">
            <div className="flex items-center gap-2">
              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-widest ${
                t.category === "Rescigo" ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#F3F4F6] text-[#5C5A55]"
              }`}>{t.category}</span>
              <span className="text-[10px] text-gray-500">· {t.read_min} min read</span>
            </div>
            <div className="font-display font-bold text-sm text-[#1A1A1A] mt-1.5 leading-tight">
              {t.title}
            </div>
            <div className="text-[12px] text-gray-700 mt-1 leading-snug">
              {t.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
